import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet,
  BarChart3,
  PlusCircle,
  TrendingUp,
  Users,
  Eye,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Megaphone
} from 'lucide-react';
import { advertiserApi, AdvertiserProfile } from '../../api';
import { useTelegram } from '../../hooks';
import { WalletSection } from './WalletSection';
import { AdsList } from './AdsList';
import { CreateAdWizard } from './CreateAdWizard';
import { AdvertiserAnalytics } from './AdvertiserAnalytics';

type Tab = 'overview' | 'ads' | 'wallet' | 'analytics';

export function AdvertiserDashboard() {
  const [profile, setProfile] = useState<AdvertiserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { hapticFeedback } = useTelegram();

  const fetchProfile = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setRefreshing(!showLoader);
      
      const response = await advertiserApi.getProfile();
      
      if (response.success && response.advertiser) {
        setProfile(response.advertiser);
        setError(null);
      } else {
        setError(response.message || 'Failed to load profile');
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setProfile(null);
        setError(null);
      } else {
        setError(err.message || 'Failed to load profile');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleRefresh = () => {
    hapticFeedback('light');
    fetchProfile(false);
  };

  const handleTabChange = (tab: Tab) => {
    hapticFeedback('light');
    setActiveTab(tab);
  };

  const handleCreateAdClick = () => {
    hapticFeedback('medium');
    setShowCreateAd(true);
  };

  const handleAdCreated = () => {
    setShowCreateAd(false);
    fetchProfile(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
        <p className="text-white/60 text-sm">Loading dashboard...</p>
      </div>
    );
  }

  if (!profile && !error) {
    return <AdvertiserRegistration onSuccess={() => fetchProfile()} />;
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <p className="text-red-400 text-sm flex-1">{error}</p>
          <button onClick={() => fetchProfile()} className="text-xs text-red-300 underline">Retry</button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="p-3 pb-24">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold text-white">Advertiser</h1>
        </div>
        <button onClick={handleRefresh} disabled={refreshing} className="p-2 rounded-lg bg-white/10">
          <RefreshCw className={`w-4 h-4 text-white/70 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="bg-primary/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-white">{profile.wallet.balance.toLocaleString()}</p>
          <p className="text-[10px] text-white/50">Balance</p>
        </div>
        <div className="bg-green-500/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-green-400">{profile.stats.activeAds}</p>
          <p className="text-[10px] text-white/50">Active</p>
        </div>
        <div className="bg-blue-500/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-blue-400">{profile.stats.totalImpressions.toLocaleString()}</p>
          <p className="text-[10px] text-white/50">Views</p>
        </div>
        <div className="bg-purple-500/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-purple-400">{profile.stats.totalCompletions.toLocaleString()}</p>
          <p className="text-[10px] text-white/50">Done</p>
        </div>
      </div>

      {/* Low Balance Warning */}
      {profile.wallet.balance < 1000 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-2 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-400" />
          <p className="text-xs text-yellow-400 flex-1">Low balance! Add funds to keep ads running.</p>
        </div>
      )}

      {/* Tab Navigation - Compact */}
      <div className="flex gap-1 mb-3 bg-white/5 rounded-lg p-1">
        {[
          { id: 'overview' as Tab, label: 'Home', icon: BarChart3 },
          { id: 'ads' as Tab, label: 'Ads', icon: Megaphone },
          { id: 'wallet' as Tab, label: 'Wallet', icon: Wallet },
          { id: 'analytics' as Tab, label: 'Stats', icon: TrendingUp },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs transition-all ${
              activeTab === tab.id ? 'bg-primary text-white' : 'text-white/60'
            }`}
          >
            <tab.icon className="w-3 h-3" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <OverviewTab profile={profile} onCreateAd={handleCreateAdClick} />
          </motion.div>
        )}
        {activeTab === 'ads' && (
          <motion.div key="ads" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <AdsList onCreateAd={handleCreateAdClick} onRefresh={() => fetchProfile(false)} />
          </motion.div>
        )}
        {activeTab === 'wallet' && (
          <motion.div key="wallet" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <WalletSection profile={profile} onRefresh={() => fetchProfile(false)} />
          </motion.div>
        )}
        {activeTab === 'analytics' && (
          <motion.div key="analytics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <AdvertiserAnalytics />
          </motion.div>
        )}
      </AnimatePresence>

      <CreateAdWizard isOpen={showCreateAd} onClose={() => setShowCreateAd(false)} onSuccess={handleAdCreated} balance={profile.wallet.balance} />
    </div>
  );
}

// Compact Overview Tab
function OverviewTab({ profile, onCreateAd }: { profile: AdvertiserProfile; onCreateAd: () => void }) {
  return (
    <div className="space-y-3">
      {/* Create Ad Button */}
      <button
        onClick={onCreateAd}
        className="w-full bg-gradient-to-r from-primary to-secondary rounded-xl p-3 flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
          <PlusCircle className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-white text-sm">Create New Ad</p>
          <p className="text-xs text-white/70">Reach thousands of users</p>
        </div>
        <ChevronRight className="w-4 h-4 text-white/70" />
      </button>

      {/* Performance Card */}
      <div className="bg-surface rounded-xl p-3 border border-white/10">
        <h3 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" />
          Performance
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white/5 rounded-lg p-2">
            <p className="text-xs text-white/50">Click Rate</p>
            <p className="text-lg font-bold text-white">
              {profile.stats.totalImpressions > 0
                ? ((profile.stats.totalClicks / profile.stats.totalImpressions) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div className="bg-white/5 rounded-lg p-2">
            <p className="text-xs text-white/50">Completion Rate</p>
            <p className="text-lg font-bold text-white">
              {profile.stats.totalClicks > 0
                ? ((profile.stats.totalCompletions / profile.stats.totalClicks) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div className="bg-white/5 rounded-lg p-2">
            <p className="text-xs text-white/50">Total Spent</p>
            <p className="text-lg font-bold text-white">{profile.wallet.totalSpent.toLocaleString()}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2">
            <p className="text-xs text-white/50">Cost/Completion</p>
            <p className="text-lg font-bold text-white">
              {profile.stats.totalCompletions > 0
                ? (profile.wallet.totalSpent / profile.stats.totalCompletions).toFixed(0)
                : 0}
            </p>
          </div>
        </div>
      </div>

      {/* Ad Status Summary */}
      <div className="bg-surface rounded-xl p-3 border border-white/10">
        <h3 className="font-semibold text-white text-sm mb-3">Ad Status</h3>
        <div className="flex gap-2">
          <div className="flex-1 text-center p-2 bg-green-500/10 rounded-lg">
            <p className="text-xl font-bold text-green-400">{profile.stats.activeAds}</p>
            <p className="text-[10px] text-white/50">Active</p>
          </div>
          <div className="flex-1 text-center p-2 bg-yellow-500/10 rounded-lg">
            <p className="text-xl font-bold text-yellow-400">{profile.stats.pendingAds}</p>
            <p className="text-[10px] text-white/50">Pending</p>
          </div>
          <div className="flex-1 text-center p-2 bg-gray-500/10 rounded-lg">
            <p className="text-xl font-bold text-gray-400">{profile.stats.completedAds}</p>
            <p className="text-[10px] text-white/50">Completed</p>
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
        <p className="text-xs text-blue-400 font-medium mb-1">💡 Tip</p>
        <p className="text-xs text-white/70">Higher rewards attract more users. Try setting rewards between 50-200 points for best results.</p>
      </div>
    </div>
  );
}

// Compact Advertiser Registration
function AdvertiserRegistration({ onSuccess }: { onSuccess: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hapticFeedback, hapticNotification } = useTelegram();

  const handleRegister = async () => {
    hapticFeedback('medium');
    setLoading(true);
    setError(null);

    try {
      const response = await advertiserApi.register();
      if (response.success) {
        hapticNotification('success');
        onSuccess();
      } else {
        setError(response.message || 'Registration failed');
        hapticNotification('error');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      hapticNotification('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 min-h-[60vh] flex flex-col items-center justify-center">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-sm">
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
          <Megaphone className="w-8 h-8 text-primary" />
        </div>

        <h2 className="text-xl font-bold text-white mb-2">Become an Advertiser</h2>
        <p className="text-white/60 text-sm mb-4">Promote your Telegram channels, bots, and apps to thousands of active users.</p>

        <div className="space-y-2 text-left mb-6">
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <Users className="w-4 h-4 text-primary" />
            <span className="text-white/80 text-sm">Target active crypto users</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <Eye className="w-4 h-4 text-primary" />
            <span className="text-white/80 text-sm">Pay only for completions</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span className="text-white/80 text-sm">Real-time analytics</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-2 mb-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={handleRegister}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <>Get Started <ChevronRight className="w-4 h-4" /></>}
        </button>
      </motion.div>
    </div>
  );
}
